import React from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Container,
  MenuItem,
  Button,
  Typography,
  Grid,
  Backdrop,
  CircularProgress,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Paper,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  FormHelperText,
} from "@material-ui/core";
import { RadioGroup, TextField } from "formik-material-ui";
import { Field, Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

import {
  useProjectGroup,
  useCustomerDetails,
  useProjectManagers,
} from "../../projectManagementQueries";
import { useCreateProject } from "../../projectManagementMutations";
import { useDispositions } from "../../../common/services/dispositionServices";
import { useFetchContext } from "../../../common/FetchContext";
import { useAuthContext } from "../../../common/AuthContext";
import { ErrorMessage } from "../../../common/ErrorMessage";
import { AddNewNote } from "../../../common/NoteOnNewDialog";
import { ViewNote } from "../../../common/ViewNoteDialog";
import { usesubmitMaxTolerance, useSubmitNote } from "../../../common/CommonMutations";
import { processErrorOnMutation } from "../../../../util/errorMessaging";
import { stringSanitize } from "../../../common/util";
import { Messaging } from "../../../common/enums";
import { useLocations } from "../../../intake/intakeQueries";
import { useProjectType } from "../../../common/CommonQueries";
import LSDBFormikAutocompleteField from "../../../common/LSDBFormikAutocompleteField";



export const NewProject = () => {
  const { authAxios } = useFetchContext();
  const { dispatch } = useAuthContext();
  const history = useHistory();
  const theme = useTheme();

  const [notes, setNotes] = React.useState<any[]>([]);
  const [viewNote, setViewNote] = React.useState(false);
  const [selectedNote, setSelectedNote] = React.useState({});

  const { customerId } = useParams() as {
    customerId: string;
  };
  const { mutateAsync: mutate } = useCreateProject();
  const { mutateAsync: mutateAddNote } = useSubmitNote();
  const { mutateAsync: mutateAddMaxTolerance } = usesubmitMaxTolerance();


  const {
    error: errorDispositions,
    data: dispositions,
    isLoading: isLoadingDispositions,
    isError: isErrorDispositions,
  } = useDispositions();

  const {
    error: errorProjectType,
    data: projectType,
    isLoading: isLoadingProjectType,
    isError: isErrorProjectType,
  } = useProjectType();

  const {
    error: errorProjectManagers,
    data: projectManagers,
    isError: isErrorProjectManagers,
  } = useProjectManagers();

  const {
    error: errorProjectGroup,
    data: projectGroup,
    isLoading: isLoadingProjectGroup,
    isError: isErrorProjectGroup,
  } = useProjectGroup();

  const {
    error: errorLocation,
    data: locationData,
    isLoading: isLoadingLocation,
    isError: isErrorLocation,
  } = useLocations();

  const {
    data: customerDetailsData,
    error: errorCustomerDetails,
    isError: isErrorCustomerDetails,
  } = useCustomerDetails(customerId);



  const handleClose = () => {
    setViewNote(!viewNote);
  };

  const handleClick = (e: any) => {
    setSelectedNote(e);
    handleClose();
  };

  if (isLoadingProjectGroup || isLoadingDispositions || isLoadingDispositions) {
    return (
      <Backdrop
        open={
          isLoadingProjectGroup ||
          isLoadingDispositions ||
          isLoadingDispositions
        }
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (
    isErrorDispositions ||
    isErrorProjectManagers ||
    isErrorProjectManagers ||
    isErrorCustomerDetails
  ) {
    return (
      <>
        {isErrorCustomerDetails && (
          <ErrorMessage error={errorCustomerDetails} />
        )}

        {isErrorDispositions && <ErrorMessage error={errorDispositions} />}
        {isErrorProjectManagers && (
          <ErrorMessage error={errorProjectManagers} />
        )}
        {isErrorProjectGroup && <ErrorMessage error={errorProjectGroup} />}
      </>
    );
  }

  return (
    <div style={theme.container}>
      <Container>
        <Grid
          container
          direction="row"
          justify="space-between"
          alignItems="center"
          spacing={4}
        >
          <Grid item xs={12}>
            <Typography variant="h5">Create New Project</Typography>
          </Grid>

          <Grid item xs={12}>
            <Formik
              initialValues={{
                number: "",
                sfdc_number: "",
                project_manager: "",
                disposition: "",
                proposal_price: "",
                project_loaction: "",
                project_type: "",
                isc: null,
                voc: null,
                pmp: null,
              }}
              validationSchema={Yup.object({
                number: Yup.string()
                  .required("Field must be required")
                  .test(
                    "sanitize",
                    Messaging.UserInput,
                    (value: string | undefined) =>
                      value ? stringSanitize(value) : false
                  ),
                sfdc_number: Yup.string()
                  .required("Field must be required")
                  .test(
                    "sanitize",
                    Messaging.UserInput,
                    (value: string | undefined) =>
                      value ? stringSanitize(value) : false
                  ),
                project_manager: Yup.string().required(
                  "Field must be required"
                ),
                disposition: Yup.string().required("Field must be required"),
                proposal_price: Yup.string().required("Field must be required"),
                project_type: Yup.string().required("Please select a project type"),
              })}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  const newProjectData = await mutate({
                    number: values.number,
                    sfdc_number: values.sfdc_number,
                    project_manager: values.project_manager,
                    disposition: values.disposition,
                    group: projectGroup.url,
                    customer: customerDetailsData.url,
                    proposal_price: values.proposal_price,
                    location: values.project_loaction,
                    project_type: values.project_type
                  });
                  await mutateAddMaxTolerance({
                    authAxios,
                    project_id: newProjectData.id,
                    isc: values.isc || 0,
                    voc: values.voc || 0,
                    pmp: values.pmp || 0,
                  });

                  if (notes.length > 0) {
                    for (let note of notes) {
                      await mutateAddNote({
                        authAxios,
                        id: newProjectData.id,
                        model: "project",
                        subject: note.subject,
                        text: note.text.toString("markdown"),
                        note_type: 1,
                        owner: 0,
                        disposition: 0,
                        labels: [],
                        groups: [],
                        tagged_users: [],
                      });
                    }
                  }

                  setSubmitting(false);
                  history.push(`/project_management/customer/${customerId}`);
                  toast.success("Project was succesfully created.");
                } catch (error) {
                  toast.error("Error while creating project");
                  processErrorOnMutation(error, dispatch, history);
                }
              }}
            >
              {({ errors, touched, submitForm, resetForm }) => (
                <Form>
                  <Grid
                    container
                    direction="row"
                    justify="space-between"
                    alignItems="center"
                    spacing={4}
                  >
                    <Grid item sm={6}>
                      <Field
                        id="number"
                        type="text"
                        name="number"
                        fullWidth
                        component={TextField}
                        helperText={touched.number ? errors.number : ""}
                        error={touched.number && Boolean(errors.number)}
                        style={{ marginBottom: 32 }}
                        label="Project Number"
                        data-testid="project_number"
                      ></Field>

                      <Field
                        name="sfdc_number"
                        helperText={
                          touched.sfdc_number ? errors.sfdc_number : ""
                        }
                        error={
                          touched.sfdc_number && Boolean(errors.sfdc_number)
                        }
                        component={TextField}
                        data-testid="sfdc_number"
                        label="SFDC Number"
                        style={{ marginBottom: 32 }}
                        placeholder="Write an SFDC number"
                        fullWidth
                        margin="dense"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      {/* <Field
                        id="project_loaction"
                        type="text"
                        name="project_loaction"
                        select={true}
                        fullWidth
                        component={TextField}
                        helperText={
                          touched.project_loaction ? errors.project_loaction : ""
                        }
                        error={
                          touched.project_loaction &&
                          Boolean(errors.project_loaction)
                        }
                        style={{ marginBottom: 32 }}
                        label="Project Location"
                        data-testid="project_loaction"
                      >
                        {locationData?.map((location: any) => (
                          <MenuItem
                            key={location.id}
                            value={location.url}
                          >
                            {location.name}
                          </MenuItem>
                        ))}
                      </Field> */}
                      <LSDBFormikAutocompleteField
                        fieldId="project_loaction"
                        fieldName="project_loaction"
                        fieldDisplayName="Project Location"
                        options={locationData}
                        getOptionLabel={(location: any) => location.name}
                        getOptionValue={(project: any) => project.url}
                        fullWidth={true}
                        variant="standard"
                      />
                      {/* <Field
                        id="project_manager"
                        type="text"
                        name="project_manager"
                        select={true}
                        fullWidth
                        component={TextField}
                        helperText={
                          touched.project_manager ? errors.project_manager : ""
                        }
                        error={
                          touched.project_manager &&
                          Boolean(errors.project_manager)
                        }
                        style={{ marginBottom: 32 }}
                        label="Project Manager"
                        data-testid="project_manager"
                      >
                        {projectManagers?.map((projectManager: any) => (
                          <MenuItem
                            key={projectManager.id}
                            value={projectManager.url}
                          >
                            {projectManager.first_name}{" "}
                            {projectManager.last_name}
                          </MenuItem>
                        ))}
                      </Field> */}
                      <LSDBFormikAutocompleteField
                        fieldId="project_manager"
                        fieldName="project_manager"
                        fieldDisplayName="Project Manager"
                        options={projectManagers}
                        getOptionLabel={(pm: any) =>
                          `${pm.first_name || ""} ${pm.last_name || ""}`
                        }
                        getOptionValue={(pm: any) => pm.url}
                        fullWidth={true}
                        variant="standard"
                      />
                      {/* <Field
                        id="disposition"
                        type="text"
                        name="disposition"
                        select={true}
                        fullWidth
                        component={TextField}
                        helperText={
                          touched.disposition ? errors.disposition : ""
                        }
                        error={
                          touched.disposition && Boolean(errors.disposition)
                        }
                        style={{ marginBottom: 32 }}
                        label="Disposition"
                        data-testid="disposition"
                      >
                        {dispositions?.map((disposition: any) => (
                          <MenuItem
                            key={disposition.id}
                            value={disposition.url}
                          >
                            {disposition.name}
                          </MenuItem>
                        ))}
                      </Field> */}
                      <LSDBFormikAutocompleteField
                        fieldId="disposition"
                        fieldName="disposition"
                        fieldDisplayName="Disposition"
                        options={dispositions}
                        getOptionLabel={(disposition: any) => disposition.name}
                        getOptionValue={(disposition: any) => disposition.url}
                        fullWidth={true}
                        variant="standard"
                      />
                      <Field
                        id="number"
                        type="number"
                        name="proposal_price"
                        fullWidth
                        component={TextField}
                        helperText={
                          touched.proposal_price ? errors.proposal_price : ""
                        }
                        error={
                          touched.proposal_price &&
                          Boolean(errors.proposal_price)
                        }
                        style={{ marginBottom: 32 }}
                        label="Contract Value"
                        data-testid="proposal_price"
                      ></Field>


                      {/* <FormControl
                        component="fieldset"
                        style={{ marginBottom: 32 }}
                        error={touched.project_type && Boolean(errors.project_type)}
                      >
                        <FormLabel component="legend">Project Type</FormLabel>
                        <Field name="project_type" component={RadioGroup} row>
                          {projectType.map((type: string) => (
                            <FormControlLabel
                              key={type}
                              value={type}
                              control={<Radio />}
                              label={type}
                            />
                          ))}
                        </Field>
                        <FormHelperText>
                          {touched.project_type ? errors.project_type : ""}
                        </FormHelperText>
                      </FormControl> */}

                      {/* <Field
                        id="project_type"
                        type="text"
                        name="project_type"
                        select={true}
                        fullWidth
                        component={TextField}
                        helperText={
                          touched.disposition ? errors.disposition : ""
                        }
                        error={
                          touched.disposition && Boolean(errors.disposition)
                        }
                        style={{ marginBottom: 32 }}
                        label="Project Type"
                        data-testid="project_type"
                      >
                        {projectType?.map((type: any) => (
                          <MenuItem
                            key={type.id}
                            value={type.name}
                          >
                            {type.name}
                          </MenuItem>
                        ))}
                      </Field> */}
                      <LSDBFormikAutocompleteField
                        fieldId="project_type"
                        fieldName="project_type"
                        fieldDisplayName="Project Type"
                        options={projectType}
                        getOptionLabel={(projectType: any) => projectType.name}
                        getOptionValue={(projectType: any) => projectType.name}
                        fullWidth={true}
                        variant="standard"
                      />
                    </Grid>

                    <Grid item sm={6} style={{ marginBottom: 0 }}>

                      <Paper
                        elevation={3}
                        style={{
                          padding: 24,
                          marginBottom: 50,
                          borderRadius: 12,
                          backgroundColor: "#ffffff",
                        }}
                      >
                        <Typography
                          variant="h6"
                          style={{
                            marginBottom: 24,
                            textAlign: "center",
                            fontWeight: 600,
                            color: "#333",
                          }}
                        >
                          Max Tolerance
                        </Typography>

                        <Grid container spacing={3}>
                          <Grid item sm={12}>
                            <Field
                              component={TextField}
                              label="ISC"
                              name="isc"
                              type="number"
                              variant="outlined"
                              fullWidth
                            />
                          </Grid>

                          <Grid item sm={12}>
                            <Field
                              component={TextField}
                              label="VOC"
                              name="voc"
                              type="number"
                              variant="outlined"
                              fullWidth
                            />
                          </Grid>

                          <Grid item sm={12}>
                            <Field
                              component={TextField}
                              label="PMP"
                              name="pmp"
                              type="number"
                              variant="outlined"
                              fullWidth
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                      <AddNewNote notes={notes} addNote={setNotes} />

                      <Paper
                        variant="outlined"
                        style={{ marginTop: 32, marginBottom: 32 }}
                      >
                        <List component="nav" aria-label="main mailbox folders">
                          {notes.map((note) => (
                            <ListItem
                              button
                              dense
                              onClick={() => handleClick(note)}
                            >
                              <ListItemText
                                primary={`Subject: ${note.subject}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Grid>

                    
                  </Grid>
                  <Grid
                    container
                    spacing={3}
                    direction="row"
                    justify="space-around"
                    alignItems="center"
                    style={{ marginTop: 32 }}
                  >
                    <Grid item xs={2}>
                      <Button
                        data-testid="submitCrateId"
                        variant="contained"
                        color="primary"
                        onClick={submitForm}
                      >
                        Submit
                      </Button>
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => resetForm()}
                      >
                        Undo Changes
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
        <ViewNote
          note={selectedNote}
          open={viewNote}
          handleClose={handleClose}
        />
      </Container>
    </div>
  );
};
